import { redirect } from '@tanstack/react-router'
import { RouterContext } from '@/routes/__root'
import { tokenRepository } from '@/domains/auth/infrastructure/repositories/TokenRepository'
import { TokenFactory } from '@/domains/auth/application/services/TokenFactory'
import { userService } from '@/features/Auth/infrastructure/services/userService'

export interface RouteGuardOptions {
  requireAuth?: boolean
  redirectTo?: string
  allowedStatuses?: Array<'loading' | 'signedIn' | 'signedOut' | 'error'>
}

const hasValidTokenSync = async (): Promise<boolean> => {
  try {
    const user = await userService.getCurrentUser()
    if (!user?.id) return false

    const { accessTokenData, refreshTokenData } = await tokenRepository.findTokensByUserId(user.id)
    
    const refreshToken = refreshTokenData 
      ? TokenFactory.createFromStoredData(refreshTokenData)
      : null
    
    if (!refreshToken || refreshToken.isExpired()) {
      return false
    }

    const accessToken = accessTokenData 
      ? TokenFactory.createFromStoredData(accessTokenData)
      : null
    
    if (!accessToken) {
      return false
    }

    return !accessToken.isExpired()
  } catch (error) {
    return false
  }
}

export const createAuthGuard = (options: RouteGuardOptions = {}) => {
  const {
    requireAuth = true,
    redirectTo = requireAuth ? '/auth/login' : '/',
    allowedStatuses,
  } = options

  return async ({ context }: { context: RouterContext }) => {
    const { authStatus, loading } = context.authContext

    if (allowedStatuses && !allowedStatuses.includes(authStatus)) {
      throw redirect({ to: redirectTo })
    }

    if (authStatus === 'loading' || loading) {
      return
    }

    if (requireAuth && authStatus !== 'signedIn') {
      throw redirect({ to: redirectTo })
    }

    if (requireAuth && authStatus === 'signedIn') {
      const hasValidToken = await hasValidTokenSync()
      if (!hasValidToken) {
        context.authContext.setAuthState({ authStatus: 'signedOut' })
        throw redirect({ to: redirectTo })
      }
    }

    if (!requireAuth && authStatus === 'signedIn') {
      throw redirect({ to: redirectTo })
    }
  }
}

export const protectedRouteGuard = createAuthGuard({
  requireAuth: true,
  redirectTo: '/auth/login',
})

export const publicRouteGuard = createAuthGuard({
  requireAuth: false,
  redirectTo: '/',
})

export const loadingOnlyGuard = createAuthGuard({
  allowedStatuses: ['loading'],
  redirectTo: '/',
})

export const customAuthGuard = (
  guardLogic: (context: RouterContext) => boolean | string | void,
) => {
  return ({ context }: { context: RouterContext }) => {
    const { authStatus, loading } = context.authContext

    if (authStatus === 'loading' || loading) {
      return
    }

    const result = guardLogic(context)

    if (typeof result === 'string') {
      throw redirect({ to: result })
    }

    if (result === false) {
      const defaultRedirect = authStatus === 'signedIn' ? '/' : '/auth/login'
      throw redirect({ to: defaultRedirect })
    }
  }
}

export const errorRouteGuard = createAuthGuard({
  allowedStatuses: ['error', 'signedOut'],
  redirectTo: '/',
})

export const conditionalAuthGuard = (
  conditions: {
    condition: (context: RouterContext) => boolean
    requireAuth: boolean
    redirectTo: string
  }[],
) => {
  return ({ context }: { context: RouterContext }) => {
    const { authStatus, loading } = context.authContext

    if (authStatus === 'loading' || loading) {
      return
    }

    for (const { condition, requireAuth, redirectTo } of conditions) {
      if (condition(context)) {
        if (requireAuth && authStatus !== 'signedIn') {
          throw redirect({ to: redirectTo })
        }
        if (!requireAuth && authStatus === 'signedIn') {
          throw redirect({ to: redirectTo })
        }
        return
      }
    }

    throw redirect({
      to: authStatus === 'signedIn' ? '/' : '/auth/login',
    })
  }
}

export const isAuthenticated = (context: RouterContext): boolean => {
  return context.authContext.authStatus === 'signedIn'
}

export const isLoading = (context: RouterContext): boolean => {
  return (
    context.authContext.authStatus === 'loading' || context.authContext.loading
  )
}

export const hasAuthError = (context: RouterContext): boolean => {
  return context.authContext.authStatus === 'error'
}

export const createAdvancedAuthGuard = (options: {
  requireAuth?: boolean
  redirectTo?: string
  allowWhileLoading?: boolean
  customCheck?: (context: RouterContext) => boolean | string
  onUnauthorized?: (context: RouterContext) => void
}) => {
  const {
    requireAuth = true,
    redirectTo = requireAuth ? '/auth/login' : '/',
    allowWhileLoading = false,
    customCheck,
    onUnauthorized,
  } = options

  return ({ context }: { context: RouterContext }) => {
    const { authStatus, loading } = context.authContext

    if (customCheck) {
      const customResult = customCheck(context)
      if (typeof customResult === 'string') {
        throw redirect({ to: customResult })
      }
      if (customResult === false) {
        onUnauthorized?.(context)
        throw redirect({ to: redirectTo })
      }
    }

    if ((authStatus === 'loading' || loading) && !allowWhileLoading) {
      return
    }

    const authenticated = authStatus === 'signedIn'

    if (requireAuth && !authenticated) {
      onUnauthorized?.(context)
      throw redirect({ to: redirectTo })
    }

    if (!requireAuth && authenticated) {
      throw redirect({ to: redirectTo })
    }
  }
}

export type AuthGuard = ReturnType<typeof createAuthGuard>
export type CustomAuthGuard = ReturnType<typeof customAuthGuard>
export type ConditionalAuthGuard = ReturnType<typeof conditionalAuthGuard>
export type AdvancedAuthGuard = ReturnType<typeof createAdvancedAuthGuard>
